// Iter 75 — Clip Request Tray (public ShareView only). Persists an array
// of {asset_id, asset_title, in_sec, out_sec, note} entries in localStorage
// scoped by share slug so a field-day session survives refreshes and
// video navigation. Submits the batch to /share/{slug}/clip-requests and
// clears the tray on success.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Film, X as XIcon, Scissors, ChevronUp, ChevronDown, Loader2, Send } from "lucide-react";
import { clipTimecodes } from "@/lib/timecode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TRAY_KEY = (slug) => `wd_clip_tray_${slug}`;

export function loadTray(slug) {
  try { return JSON.parse(localStorage.getItem(TRAY_KEY(slug)) || "[]"); }
  catch { return []; }
}

export function saveTray(slug, clips) {
  localStorage.setItem(TRAY_KEY(slug), JSON.stringify(clips));
  window.dispatchEvent(new CustomEvent("wd:clip-tray-updated", { detail: { slug } }));
}

export default function ClipRequestTray({ slug, clips, onChange }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem("wd_share_name") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("wd_share_email") || "");
  const [busy, setBusy] = useState(false);

  if (!clips.length) return null;

  const remove = (idx) => {
    const next = clips.filter((_, i) => i !== idx);
    saveTray(slug, next);
    onChange(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const payload = {
        requester_name: name.trim(),
        requester_email: email.trim(),
        clips: clips.map((c) => ({ asset_id: c.asset_id, in_sec: c.in_sec, out_sec: c.out_sec, note: c.note || "" })),
      };
      await axios.post(`${API}/share/${slug}/clip-requests`, payload);
      localStorage.setItem("wd_share_name", name.trim());
      localStorage.setItem("wd_share_email", email.trim());
      saveTray(slug, []);
      onChange([]);
      setOpen(false);
      toast.success(`Request submitted · ${clips.length} clip${clips.length === 1 ? "" : "s"} queued for the editing team`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit the request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="clip-request-tray"
      className="fixed bottom-4 right-4 z-50 w-[min(28rem,calc(100vw-2rem))]"
    >
      {open && (
        <div className="mb-2 bg-zinc-950/95 backdrop-blur-md border border-zinc-800 rounded-xl shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
              <Scissors className="w-4 h-4 text-[var(--brand-accent)]" />
              Clip request · {clips.length}
            </div>
            <button
              data-testid="clip-tray-close"
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-zinc-200 transition"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="max-h-[40vh] overflow-y-auto">
            {clips.map((c, i) => (
              <div
                key={i}
                data-testid={`clip-tray-row-${i}`}
                className="px-4 py-2.5 border-b border-zinc-900 flex items-start gap-3 hover:bg-white/[0.02] transition"
              >
                <Film className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-100 font-medium truncate">{c.asset_title}</div>
                  <div className="text-[11px] text-zinc-500 tabular-nums mt-0.5" data-testid={`clip-tray-tc-${i}`}>
                    {(() => {
                      const t = clipTimecodes(c);
                      return <>In <span className="text-[var(--brand-accent)]">{t.inTc}</span> → Out <span className="text-[var(--brand-accent)]">{t.outTc}</span> · {t.length}{t.fpsText && <span className="text-zinc-600"> · {t.fpsText}</span>}</>;
                    })()}
                  </div>
                  {c.note && <div className="text-[11px] text-zinc-400 mt-1 italic truncate">“{c.note}”</div>}
                </div>
                <button
                  data-testid={`clip-tray-remove-${i}`}
                  onClick={() => remove(i)}
                  title="Remove this clip"
                  className="text-zinc-600 hover:text-red-400 transition"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="p-4 border-t border-zinc-800 space-y-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              data-testid="clip-tray-name"
              required
              className="w-full bg-black/40 border border-zinc-800 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Your email (where we'll reply)"
              data-testid="clip-tray-email"
              required
              className="w-full bg-black/40 border border-zinc-800 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60"
            />
            <button
              type="submit"
              disabled={busy || !name.trim() || !email.trim()}
              data-testid="clip-tray-submit"
              className="w-full flex items-center justify-center gap-2 bg-[var(--brand-accent)] text-black font-semibold rounded-md px-4 py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Submit {clips.length} clip{clips.length === 1 ? "" : "s"}
            </button>
          </form>
        </div>
      )}

      <button
        data-testid="clip-tray-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 bg-[var(--brand-accent)] text-black font-semibold rounded-full px-4 py-3 shadow-lg hover:bg-[var(--brand-accent)]/90 transition"
      >
        <span className="flex items-center gap-2">
          <Scissors className="w-4 h-4" />
          {clips.length} clip{clips.length === 1 ? "" : "s"} queued
        </span>
        <span className="flex items-center gap-1 text-xs opacity-80">
          Review tray {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        </span>
      </button>
    </div>
  );
}
